import { consumer, core, payloadToJSON, toJSON } from '@tock/coretypes';
import { customDimensions } from 'utils/Analytics';
import { getAvailabilityStats } from 'utils/calendar';
import AppConstants from 'constants/AppConstants';

interface BusinessCalendarDay {
  ticketGroupByTicketType?: Dictionary<consumer.ICalendarTicketGroup[]>;
}

export interface ConsumerBusinessCalendar {
  ticketGroupsByDayByTicketType?: Dictionary<BusinessCalendarDay>;
  ticketGroup?: consumer.ICalendarTicketGroup[];
  ticketType: core.ITicketType[];
  ticketTypeCalendarDetails: consumer.ITicketTypeCalendarDetails[];
  openDate: string[];
  createdAt: string;
}

interface State {
  ticketTypeAccessToken?: string;
  attemptedOnce: boolean;
  calendar: ConsumerBusinessCalendar | null;
  offerings: consumer.ConsumerOfferings | null;
  offeringsInProgress: boolean;
  requestInProgress: boolean;
  experienceDetail: {
    offering?: consumer.Offering;
    requestInProgress: boolean;
    requestFailed: boolean;
  };
  businessId?: string;
}

export const initialState: State = {
  attemptedOnce: false,
  ticketTypeAccessToken: undefined,
  calendar: null,
  offerings: null,
  offeringsInProgress: false, // This comes from server side with actual data
  requestInProgress: true, // This comes from server as loading state
  experienceDetail: {
    offering: undefined,
    requestInProgress: false,
    requestFailed: false,
  },
};

function setAvailabilitySegment(calendar: consumer.ConsumerFullCalendar) {
  const { available, unavailable } = getAvailabilityStats(calendar);
  const total = available + unavailable;
  if (total === 0) {
    return;
  }
  const percentage = (available * 100) / total;
  const bucket = bucketize(percentage, 5);
  // tslint:disable:strict-type-predicates
  if (window.analytics) {
    window.analytics.setDimensionInTockOnly(customDimensions.BOOKED_PERCENT, bucket.toString());
  }
}

function setNumExperiencesSegment(calendarJson: consumer.ConsumerFullCalendar) {
  if (!calendarJson || !calendarJson.ticketType) {
    return;
  }
  const numExperiences = calendarJson.ticketType.length;
  // tslint:disable:strict-type-predicates
  if (window.analytics) {
    window.analytics.setDimensionInTockOnly(
      customDimensions.NUM_EXPERIENCES,
      numExperiences.toString()
    );
  }
}

// Puts a number in its "bucket"; e.g. if you want 5-point-wide buckets, then 95-99 all go to the "95" bucket.
function bucketize(n: number, bucketSize: number): number {
  return Math.floor(n / bucketSize) * bucketSize;
}

export default function reducer(state: State = initialState, action: BaseTockAction): State {
  const { type, payload, context } = action;
  switch (type) {
    case AppConstants.CALENDAR_FETCH_DONE: {
      const calendarJson = payloadToJSON<consumer.ConsumerFullCalendar>(payload);

      // as a janky side effect, ping Google Analytics to set the session's availability percent.
      // TODO: Move this out of state.
      if (BROWSER) {
        requestIdleCallback(() => {
          setAvailabilitySegment(calendarJson);
          setNumExperiencesSegment(calendarJson);
        });
      }

      return {
        ...state,
        calendar: calendarJson,
        ticketTypeAccessToken: context?.ticketTypeAccessToken,
        requestInProgress: false,
        businessId: context?.businessId,
      };
    }
    case AppConstants.CALENDAR_FETCH_V2_DONE: {
      const calendarV2Json = payloadToJSON<consumer.ConsumerFullCalendarV2>(payload);

      const flatTicketGroups: consumer.ICalendarTicketGroup[] = [];
      const ticketGroupsByDayByTicketType: Dictionary<BusinessCalendarDay> = {};

      // todo clean up this code
      Object.entries(calendarV2Json?.ticketGroupByBusinessDay ?? {}).forEach(
        ([businessDay, ticketGroupByDate]) => {
          if (ticketGroupByDate === undefined || ticketGroupByDate === null) {
            return;
          }

          Object.entries(ticketGroupByDate.ticketGroupByDate || {}).forEach(
            ([date, ticketGroupList]) => {
              ticketGroupList.ticketGroup?.forEach((tg) => {
                const ticketGroupUnpacked = { ...tg, businessDay, date };
                flatTicketGroups.push(ticketGroupUnpacked);
                const ticketTypeId = tg.ticketTypePrice?.[0].ticketTypeId;
                if (!ticketTypeId) {
                  return;
                }
                const existingBusinessDay = ticketGroupsByDayByTicketType[businessDay] || {};
                const existingTicketGroupsByTicketType =
                  existingBusinessDay?.ticketGroupByTicketType || {};
                const existingTicketTypeTicketGroups =
                  existingBusinessDay?.ticketGroupByTicketType?.[ticketTypeId] || [];

                const newTicketTypeTicketGroups = [
                  ...existingTicketTypeTicketGroups,
                  ticketGroupUnpacked,
                ];
                const newTicketGroupByTicketType = {
                  ...existingTicketGroupsByTicketType,
                  [ticketTypeId]: newTicketTypeTicketGroups,
                };

                ticketGroupsByDayByTicketType[businessDay] = {
                  ticketGroupByTicketType: newTicketGroupByTicketType,
                };
              });
            }
          );
        }
      );

      return {
        ...state,
        calendar: {
          ...calendarV2Json,
          ticketGroup: flatTicketGroups,
          ticketGroupsByDayByTicketType,
        },
        ticketTypeAccessToken: context?.ticketTypeAccessToken,
        requestInProgress: false,
        businessId: context?.businessId,
      };
    }
    case AppConstants.OFFERINGS_FETCH_DONE: {
      const offerings = toJSON<consumer.ConsumerOfferings>(payload);
      return {
        ...state,
        offerings,
        offeringsInProgress: false,
        attemptedOnce: true,
        ticketTypeAccessToken: context?.ticketTypeAccessToken,
      };
    }
    case AppConstants.CALENDAR_FETCH_V2_STARTING:
    case AppConstants.CALENDAR_FETCH_STARTING: {
      return {
        ...state,
        requestInProgress: true,
      };
    }
    case AppConstants.OFFERINGS_FETCH_STARTING: {
      return {
        ...state,
        offeringsInProgress: true,
      };
    }
    case AppConstants.CALENDAR_FETCH_V2_FAILED:
    case AppConstants.CALENDAR_FETCH_FAILED: {
      return {
        ...state,
        requestInProgress: false,
      };
    }
    case AppConstants.OFFERINGS_FETCH_FAILED: {
      return {
        ...state,
        offeringsInProgress: false,
        attemptedOnce: true,
      };
    }
    case AppConstants.OFFERING_FETCH_STARTING: {
      return {
        ...state,
        experienceDetail: {
          requestInProgress: true,
          requestFailed: false,
        },
      };
    }
    case AppConstants.OFFERING_FETCH_DONE: {
      const offering = toJSON<consumer.Offering>(payload);
      return {
        ...state,
        experienceDetail: {
          offering,
          requestInProgress: false,
          requestFailed: false,
        },
      };
    }
    case AppConstants.OFFERING_FETCH_FAILED: {
      return {
        ...state,
        experienceDetail: {
          requestInProgress: false,
          requestFailed: true,
        },
      };
    }
    case AppConstants.CALENDAR_AND_OFFERINGS_CLEAR: {
      return {
        ...state,
        calendar: null,
        offerings: null,
        offeringsInProgress: false,
        requestInProgress: false,
        experienceDetail: initialState.experienceDetail,
      };
    }
    default:
      return state;
  }
}
